
import React, { useState, useEffect } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import { SidebarProvider } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: React.ReactNode;
  fullWidth?: boolean;
  hideHeader?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, fullWidth = false, hideHeader = false }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    // Ensure component is fully mounted before rendering children
    setIsRendered(true);

    // Log to help with debugging
    console.log('Layout component mounted');
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (fullWidth) {
    return (
      <SidebarProvider>
        <div className="flex h-screen overflow-hidden w-full bg-[#fdfcfb] selection:bg-harvest-primary/30">
          <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
          <div className={cn(
            "flex flex-col flex-1 overflow-hidden transition-all duration-500 ease-in-out",
            sidebarOpen ? 'ml-64' : 'ml-20'
          )}>
            {!hideHeader && <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />}
            <main className="flex-1 overflow-y-auto relative">
              <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
              <div className="w-full h-full animate-in fade-in duration-700">
                {isRendered && children}
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen overflow-hidden w-full bg-[#fdfcfb] selection:bg-harvest-primary/30">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className={cn(
          "flex flex-col flex-1 overflow-hidden transition-all duration-500 ease-in-out",
          sidebarOpen ? 'ml-64' : 'ml-20'
        )}>
          {!hideHeader && <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />}
          <main className="flex-1 overflow-y-auto bg-transparent p-4 md:p-8 relative">
            <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_70%,transparent_100%)] -z-10 opacity-40" />
            <div className="mx-auto w-full max-w-7xl animate-in slide-in-from-bottom-4 duration-700">
              {isRendered && children}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Layout;
